import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import { BrowseEditComponent } from '../../browse/components/browse-edit/browse-edit.component';
import { GeoTagComponent } from '../../map/geo-tag/geo-tag.component';

import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

/**
 * Component providing an edit view of a product's metadata.
 */
@Component({
	selector: 'metadata-edit',
	templateUrl: './metadata-edit.component.html',
	styleUrl: './metadata-edit.component.css'
})
export class MetadataEditComponent implements OnInit, OnChanges, OnDestroy {
	@Input() productId: any;

	@Output() triggerCancel = new EventEmitter<{ componentName: string }>();

	LOCALIZATION: any;

	editMetadataObj: any = {
		fields: [],    // form fields
		metadata: {}   // product metadata
	};

	library: string | undefined = undefined;
	metadata_raw: any = {};
	controlledFields: any[] = [];
	dateFormat: string = 'MM/dd/yyyy';
	inprogress: boolean = false;

	constructor(
		private userService: UserService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private searchService: SearchService,
		private ngbModal: NgbModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.dateFormat = this.userService.getDateFormat();
		this.initializeForm();
	}

	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		const change = changes['productId'];
		if (
			change &&
			change.previousValue &&
			change.previousValue !== change.currentValue
		) {
			this.initializeForm();
		}
	}

	ngOnDestroy(): void {
		this.handleDestroy();
	}

	private async handleDestroy(): Promise<void> {
		const locked = await this.isProductLockedByThisUser();
		if (locked) {
			this.unlockProduct();
		}
	}

	private async initializeForm(): Promise<any> {
		this.editMetadataObj.fields = [];
		this.editMetadataObj.metadata = {};

		const success = await this.lockProduct();
		if (success) {
			this.library = await this.getLibrary();
			const form = await this.getFormTemplate();
			const schema = await this.getMetadataSchema(this.library);
			this.metadata_raw = await this.getMetadata();

			if (!form || !schema) {
				this.alertsService.error({
					message: "Failed to get the edit form for this product."
				});
			}
			else if (!this.metadata_raw) {
				this.alertsService.error({
					message: "Failed to get the metadata for this product."
				});
			}
			else {
				this.loadFields(form, schema);
			}
		}
	}

	private async getMetadata(): Promise<any | undefined> {
		try {
			let metadata = await this.productService.getMetadata(this.productId);

			if (metadata.Product && metadata.Product.aw_field) {
				return metadata;
			}

			console.error("Failed to get product metadata. Unexpected object structure.");
			return undefined;
		} catch (err) {
			console.error("Failed to get product metadata: ", err);
			return undefined;
		}
	}

	private async getFormTemplate(): Promise<any | undefined> {
		try {
			return await this.productService.getMetadataFormForDocument(this.productId);
		} catch (err) {
			console.error("Failed to get product metadata edit form: ", err);
			return undefined;
		}
	}

	private async getMetadataSchema(libraryName: string | undefined): Promise<any | undefined> {
		try {
			return await this.productService.getMetadataSchema(libraryName);
		} catch (err) {
			console.error("Failed to get metadata schema for library: ", err);
			return undefined;
		}
	}

	private async getLibrary(): Promise<string | undefined> {
		try {
			return await this.productService.getDocumentLibrary(this.productId);
		} catch (err) {
			console.error("Failed to get library for product: ", err);
			return undefined;
		}
	}

	private async isProductLockedByThisUser(): Promise<boolean> {
		try {
			const lock = await this.productService.isLocked(this.productId);
			if (!lock) {
				return false;
			}
			if (!lock.lockType) {
				return false;
			}

			console.log(`Product locked by ${lock.lockedBy}`);
			if (this.userService.user.username === lock.lockedBy) {
				return true;
			}

			return false;
		} catch (err) {
			console.error("Failed to get product lock status: ", err);
			return Promise.reject();
		}
	}

	private async lockProduct(): Promise<boolean> {
		try {
			const response = await this.productService.lockProduct(this.productId);
			return true;
		} catch (err) {
			console.error("Failed to lock the product: ", err);
			this.alertsService.error({
				title: this.LOCALIZATION.lockFailed,
				message: err
			});
			return false;
		}
	}

	private unlockProduct(): void {
		this.productService.unlockProduct(this.productId).then(
			(response: any) => {
				// Do nothing
			},
			(err: any) => {
				console.error("Failed to unlock the product: ", err);
			}
		);
	}

	private loadFields(form: any, schema: any): void {
		console.log(form);
		console.log(schema);

		this.controlledFields = schema.properties;

		const searchInputs = this.userService.user.searchInputs || [];
		let clonedSearchInputs = cloneDeep(searchInputs);

		// TODO This can be moved to fields service
		for (let i = 0; i < form.fields.length; i++) {
			let formItem = form.fields[i];

			let schemaObj = this.controlledFields[formItem.name];
			if (!schemaObj) continue;

			let userFormField = clonedSearchInputs.find((x: any) => { return x.name === formItem.name || x.form_name === formItem.name });
			if (userFormField) {
				userFormField.name = userFormField.form_name || userFormField.name;

				this.editMetadataObj.fields.push(userFormField);

				if (Object.keys(userFormField.dependents).length > 0) {
					userFormField.isdependent = true;
				}
			}
			else {
				console.log(`Form field ${formItem.name} is not in user's profile!`);
			}

			// Decorate the fields for form usage
			if (userFormField) {
				userFormField.hidden = false;
				userFormField.min = formItem.min;
				userFormField.max = formItem.max;
				userFormField.readOnly = formItem.readOnly;
				userFormField.required = formItem.required;
				userFormField.show = formItem.show;

				if (schemaObj.items) {
					userFormField.ismultiple = true;
				}

				// Normalize the form field type
				userFormField.type = userFormField.type_normalized || "STRING";
				if ("INTEGER" === userFormField.type_normalized || "FLOAT" === userFormField.type_normalized || "DECIMAL" === userFormField.type_normalized || "LONG" === userFormField.type_normalized) {
					userFormField.type = "NUMBER";
				}
				else if ("LIST_MULTI" === userFormField.type_normalized || "RADIO" === userFormField.type_normalized) {
					userFormField.type = "LIST";
				}
			}

		}

		this.populateFields();
	}

	populateFields(): void {
		let productInfo: any = {};
		let fields = Array.isArray(this.metadata_raw.Product.aw_field) ? this.metadata_raw.Product.aw_field : [this.metadata_raw.Product.aw_field];
		for (let i = 0; i < fields.length; i++) {
			let field = fields[i].name;
			let editFieldObject = this.editMetadataObj.fields.find((formField: any) => formField.name.toLowerCase() === field.toLowerCase());
			if (editFieldObject) {
				field = editFieldObject.name;
			}

			let schemaObj = this.controlledFields[field] || {};
			if (productInfo[field]) {
				if (schemaObj && schemaObj.collection && schemaObj.collection.type && schemaObj.collection.type.toLowerCase() === "LIST_MULTI") {
					productInfo[field][0].push(fields[i].content);
				} else if (editFieldObject && editFieldObject.type === "NUMBER") {
					let num = fields[i].content * 1;
					if (isNaN(num)) { num = fields[i].content; }
					productInfo[field].push(num);
				} else {
					productInfo[field].push(fields[i].content);
				}
			} else {
				if (schemaObj && schemaObj.collection && schemaObj.collection.type && schemaObj.collection.type.toLowerCase() === "LIST_MULTI") {
					productInfo[field] = [fields[i].content];
				} else if (editFieldObject && editFieldObject.type === "NUMBER") {
					let num = fields[i].content * 1;
					if (isNaN(num)) { num = fields[i].content; }
					productInfo[field] = [num];
				} else {
					productInfo[field] = [fields[i].content];
				}
			}
		}

		this.editMetadataObj.metadata = productInfo;
		for (let i = 0; i < this.editMetadataObj.fields.length; i++) {
			this.editMetadataObj.fields[i].append = [{ append: "" }]
			if (!this.editMetadataObj.metadata[this.editMetadataObj.fields[i].name]) {
				// Do we need this LIST logic?
				//				if (this.editMetadataObj.fields[i].type === "LIST") {
				//					if (this.editMetadataObj.fields[i].filteredOptions && this.editMetadataObj.fields[i].filteredOptions.length > 0) {
				//						this.editMetadataObj.metadata[this.editMetadataObj.fields[i].name] = ["" + this.editMetadataObj.fields[i].filteredOptions[0].value];
				//					}
				//				} else {
				//					this.editMetadataObj.metadata[this.editMetadataObj.fields[i].name] = [""];
				//				}
				this.editMetadataObj.metadata[this.editMetadataObj.fields[i].name] = [""];
			}
		}

		console.log("fields");
		console.log(this.editMetadataObj.fields);
		console.log("metadata");
		console.log(this.editMetadataObj.metadata);
	}

	private parseToDate(value: any): Date | null {
		if (value instanceof Date && !isNaN(value.getTime())) {
			return value;
		}

		const parsed = dayjs(value, this.dateFormat, true);
		return parsed.isValid() ? parsed.toDate() : value;
	}

	saveProductMetadata(): void {
		console.log("Saving metadata. Fields:");
		console.log(this.editMetadataObj);

		this.inprogress = true;

		let metadata: { [key: string]: any[] } = {};

		// Traverse and get only values that aren't empty
		for (let i = 0; i < this.editMetadataObj.fields.length; i++) {
			let field = this.editMetadataObj.fields[i];
			for (let j = 0; j < this.editMetadataObj.metadata[field.name].length; j++) {
				let value = this.editMetadataObj.metadata[field.name][j];
				if (value && value != "") {

					// Attach calendar input values as date objects
					if (field.type === 'DATE') {
						let dateValue = this.parseToDate(value);
						value = dateValue;
					}

					if (metadata[field.name]) {
						metadata[field.name].push(value);
					} else {
						if (value instanceof Array) {
							metadata[field.name] = value;
						} else {
							metadata[field.name] = [value];
						}
					}
				}
			}
		}

		// Set empty metadata
		for (let i = 0; i < this.editMetadataObj.fields.length; i++) {
			let field = this.editMetadataObj.fields[i];
			for (let j = 0; j < this.editMetadataObj.metadata[field.name].length; j++) {
				if (!metadata[field.name]) {
					metadata[field.name] = [""];
				}
			}
		}

		let payload = {
			id: this.productId,
			replaceAll: false,
			replaceFieldValues: true,
			metadata: metadata
		};

		console.log("Saving metadata. payload:");
		console.log(payload);

		this.productService.updateMetadata(payload).then(
			(response: any) => {
				this.inprogress = false;
				this.alertsService.success({
					message: this.LOCALIZATION.metadataUpdatedMessage + " " + this.LOCALIZATION.searchUpdatesConfirmationMessage,
					delay: 5000
				});
			},
			(err: any) => {
				console.error("Failed to update product metadata: ", err);
				this.inprogress = false;
				this.alertsService.error({
					message: "Failed to update metadata."
				});
			}
		);
	}

	/**
	 * Opens a browse editor for the field.
	 */
	// Bound in the child template
	onBrowseEdit(fieldName: string, index: number): void {
		let browsepath = {
			lib_name: this.library,
			path: this.editMetadataObj.metadata[fieldName] // Submits all paths, not just the path corresponding to the passed index
		};
		let productGroup = {
			records: [this.editMetadataObj.metadata],
			ids: [this.productId]
		};

		const modalRef = this.ngbModal.open(BrowseEditComponent, {
			centered: true
		});

		modalRef.componentInstance.browsepath = browsepath;
		modalRef.componentInstance.productGroup = productGroup;

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.editMetadataObj.metadata[fieldName] = payload.result.caller.data;
				}
			},
			(reason) => {
				console.log('Modal dismissed:', reason);
			}
		);
	}

	/**
	 * Opens a geotagger for the field.
	 */
	// Bound in the child template
	onGeotag(fieldName: string, index: number): void {
		const modalRef = this.ngbModal.open(GeoTagComponent, {
			size: 'lg',
			windowClass: 'kv-utility-modal kv-utility-large-modal-window',
			animation: false
		});

		modalRef.componentInstance.productGroup = {
			commit: false,
			records: [this.productId]
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					let result = payload.result.caller.result;
					if (Array.isArray(result)) {
						this.editMetadataObj.metadata[fieldName] = result;
					} else {
						this.editMetadataObj.metadata[fieldName] = [result];
					}
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	cancel(): void {
		this.unlockProduct();
		this.triggerCancel.emit({ componentName: 'metadata-edit' });
	}
}